import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Select, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { listJobs, retryJob } from '../api/job';
import { Topbar } from '../layouts/Topbar';
import { JobStatusBadge } from '../components/StatusBadge';
import type { JobStatus, JobType } from '../api/types';
import { formatDateTime } from '../utils/datetime';
import { useAuthStore } from '../store/auth';

export function JobsPage() {
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const [status, setStatus] = useState<JobStatus | undefined>();
  const [jobType, setJobType] = useState<JobType | undefined>();
  const [tenantCode, setTenantCode] = useState('');
  const qc = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ['jobs', { status, jobType, tenantCode }],
    queryFn: () => listJobs({ status, type: jobType, tenantCode: tenantCode || undefined }),
    refetchInterval: 10_000,
  });

  const retry = useMutation({
    mutationFn: retryJob,
    onSuccess: () => { message.success('已重试'); qc.invalidateQueries({ queryKey: ['jobs'] }); },
    onError: (e: any) => message.error(e.response?.data?.message ?? '重试失败'),
  });

  return (
    <>
      <Topbar crumbs={[{ label: '运营' }, { label: '任务监控' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title"><h1>任务监控</h1><div className="page-header__sub">所有 provision / migrate / backup / meta_backup / restore / purge / sweeper / metrics_sync 异步任务。</div></div>
          <div className="page-header__actions">
            <button className="btn btn-secondary" onClick={() => refetch()}>刷新</button>
          </div>
        </div>

        <div className="card">
          <div className="card__body" style={{ paddingBottom: 0 }}>
            <div className="toolbar">
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type="search"
                  placeholder="搜索租户 code…"
                  value={tenantCode}
                  onChange={(e) => setTenantCode(e.target.value)}
                  style={{ paddingLeft: 36, minWidth: 220 }}
                />
                <SearchOutlined style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-fg-3)' }} />
              </div>
              <Select placeholder="全部状态" allowClear value={status} onChange={setStatus} style={{ minWidth: 140 }}
                options={[{ value: 'pending', label: '排队中' }, { value: 'running', label: '运行中' }, { value: 'succeeded', label: '成功' }, { value: 'failed', label: '失败' }]} />
              <Select placeholder="全部类型" allowClear value={jobType} onChange={setJobType} style={{ minWidth: 160 }}
                options={[
                  { value: 'provision', label: 'provision' }, { value: 'migrate', label: 'migrate' },
                  { value: 'backup', label: 'backup' }, { value: 'meta_backup', label: 'meta_backup' },
                  { value: 'restore', label: 'restore' }, { value: 'purge', label: 'purge' },
                  { value: 'status_sweep', label: 'status_sweep' }, { value: 'metrics_sync', label: 'metrics_sync' },
                ]} />
              {tenantCode && (
                <span className="filter-chip">
                  租户: {tenantCode}
                  <span className="filter-chip__x" onClick={() => setTenantCode('')}>×</span>
                </span>
              )}
              <div className="toolbar__right text-xs text-muted">自动刷新 10s</div>
            </div>
          </div>
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table table--fixed jobs-table">
              <colgroup>
                <col style={{ width: 86 }} />
                <col style={{ width: 96 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 104 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 72 }} />
                <col style={{ width: 72 }} />
                <col style={{ width: 260 }} />
                <col style={{ width: 96 }} />
              </colgroup>
              <thead><tr>
                <th>Job ID</th><th>租户</th><th>类型</th><th>状态</th>
                <th>调度</th><th>开始</th><th>结束</th><th>耗时</th><th>尝试</th><th>详情</th><th className="actions">操作</th>
              </tr></thead>
              <tbody>
                {data?.items.map((j) => (
                  <tr key={j.id}>
                    <td className="text-mono">#{j.id}</td>
                    <td className="text-mono">{j.tenant_id == null
                      ? <span className="tag" style={{ fontFamily: 'var(--font-sans)' }}>平台级</span>
                      : (j.tenant_code ?? `#${j.tenant_id}`)}</td>
                    <td><span className="tag">{j.job_type}</span></td>
                    <td><JobStatusBadge status={j.status} /></td>
                    <td className="text-mono num text-xs" title={j.scheduled_at}>{formatDateTime(j.scheduled_at)}</td>
                    <td className="text-mono num text-xs" title={j.started_at ?? undefined}>{formatDateTime(j.started_at)}</td>
                    <td className="text-mono num text-xs" title={j.finished_at ?? undefined}>{j.finished_at ? formatDateTime(j.finished_at) : (j.status === 'running' ? <span style={{ color: 'var(--c-info)' }}>运行中</span> : '—')}</td>
                    <td className="num">—</td>
                    <td className="num">{j.attempts}</td>
                    <td className="text-xs">
                      {j.last_error ? <span className="table-cell-ellipsis" title={j.last_error} style={{ color: 'var(--c-destructive)' }}>{j.last_error}</span>
                        : (() => {
                            const p = j.payload as Record<string, unknown>;
                            const detail = (p?.backup_key ?? p?.restored_from ?? p?.backup_path) as string | undefined;
                            return detail
                              ? <span className="text-mono text-muted table-cell-ellipsis" title={detail}>{detail}</span>
                              : '—';
                          })()}
                    </td>
                    <td className="actions">
                      {isAdmin && j.status === 'failed' && <button className="btn btn-secondary btn-sm" onClick={() => retry.mutate(j.id)}>重试</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
